import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Phone, ShieldCheck, Award, Clock, Hammer, Plug, Lightbulb, BatteryCharging, FileSearch, Zap, Check, Star } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import PortfolioImage from "@/components/PortfolioImage";

const services = [
  { id: "installation", icon: Plug, title: "Installation neuve", code: "01", text: "Installation complète conforme NF C 15-100. Tableaux, câblage, mise en service Consuel.", span: "md:col-span-5 md:row-span-2" },
  { id: "renovation", icon: Hammer, title: "Rénovation électrique", code: "02", text: "Mise aux normes et modernisation des installations existantes.", span: "md:col-span-4" },
  { id: "depannage", icon: Zap, title: "Dépannage urgent 24/7", code: "03", text: "Intervention rapide pour pannes, courts-circuits et disjoncteurs défaillants.", span: "md:col-span-3" },
  { id: "eclairage", icon: Lightbulb, title: "Éclairage", code: "04", text: "Conception architecturale, LED et domotique pour intérieur et extérieur.", span: "md:col-span-3" },
  { id: "bornes", icon: BatteryCharging, title: "Bornes IRVE", code: "05", text: "Installation de bornes de recharge pour véhicules électriques.", span: "md:col-span-4" },
  { id: "diagnostic", icon: FileSearch, title: "Diagnostic & Conformité", code: "06", text: "Audit complet, certification et mise en sécurité de votre installation.", span: "md:col-span-5" },
];

export default function HomePage() {
  const [testimonials, setTestimonials] = useState([]);
  const [portfolio, setPortfolio] = useState([]);

  const loadTestimonials = () => {
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
  };

  useEffect(() => {
    loadTestimonials();
    api.get("/portfolio").then((r) => setPortfolio(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Hero />
      <TrustBar />
      <Services />
      <PortfolioPreview items={portfolio} />
      <About />
      <Testimonials items={testimonials} onReload={loadTestimonials} />
      <ContactSection />
    </div>
  );
}

function Hero() {
  const chaletUrl = "/img/chalet.jpg";
  return (
    <section className="relative pt-32 pb-24 md:pt-40 md:pb-32 text-white overflow-hidden bg-graphite" data-testid="hero-section">
      <div className="absolute inset-0">
        <img src={chaletUrl} alt="Chalet illuminé — réalisation AMINE-ELEC" className="w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/85 via-[#0F172A]/40 to-transparent" />
        <div className="absolute inset-0 bg-[#0F172A]/35 md:hidden" />
      </div>
      <div className="container-page relative z-10">
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7 fade-in">
            <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-7xl leading-[0.95]">
              L'électricité<br/>
              <span className="copper">faite avec</span> précision.
            </h1>
            <p className="mt-8 text-base md:text-lg text-white/85 max-w-xl leading-relaxed">
              Installation, rénovation et dépannage pour particuliers et professionnels.
              Une équipe qualifiée qui livre des chantiers propres, dans les délais, conformes aux normes.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <a href="#contact" className="btn-copper" data-testid="hero-cta-devis">
                Demander un devis
                <ArrowUpRight size={18} strokeWidth={2.5} />
              </a>
              <a href="tel:+33641287492" className="btn-ghost-dark" data-testid="hero-cta-phone">
                <Phone size={16} strokeWidth={2.5} />
                06 41 28 74 92
              </a>
            </div>
          </div>

          <div className="md:col-span-5 fade-in">
            <div className="border-t border-white/20 pt-6 grid grid-cols-3 bg-[#0F172A]/40 backdrop-blur-sm p-6">
              <div data-testid="stat-experience">
                <div className="font-display text-3xl md:text-4xl font-bold copper">20+</div>
                <div className="overline text-white/70 mt-1">Années</div>
              </div>
              <div data-testid="stat-clients">
                <div className="font-display text-3xl md:text-4xl font-bold copper">1 000+</div>
                <div className="overline text-white/70 mt-1">Clients</div>
              </div>
              <div data-testid="stat-urgence">
                <div className="font-display text-3xl md:text-4xl font-bold copper">24h</div>
                <div className="overline text-white/70 mt-1">Urgences</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustBar() {
  const items = [
    "Qualifelec",
    "Garantie décennale",
    "Devis gratuit · 48h",
    "Consuel",
    "Artisan déclaré",
    "Assurance pro",
    "IRVE niveau 2",
    "Auvergne-Rhône-Alpes",
  ];
  return (
    <section className="bg-graphite text-white/70 border-y border-white/10 py-6 marquee" data-testid="trust-bar">
      <div className="marquee-track">
        {[...items, ...items].map((it, i) => (
          <span key={i} className="inline-flex items-center gap-3 overline">
            <ShieldCheck size={14} className="copper" />
            {it}
          </span>
        ))}
      </div>
    </section>
  );
}

function Services() {
  return (
    <section id="services" className="section-pad bg-background" data-testid="services-section">
      <div className="container-page">
        <div className="grid md:grid-cols-12 gap-6 mb-14">
          <div className="md:col-span-5">
            <div className="overline copper mb-4">Nos services · 06</div>
            <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight leading-[1.05]">
              Six expertises pour<br/>tous vos chantiers.
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 md:pt-3">
            <p className="text-muted-foreground leading-relaxed">
              Du diagnostic au Consuel, nous prenons en charge l'intégralité de votre projet électrique :
              maison individuelle, copropriété, commerce, bureaux ou local industriel.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-8 gap-4 md:auto-rows-[180px]">
          {services.map((s) => (
            <div
              key={s.id}
              className={`group border border-border bg-card p-7 flex flex-col justify-between transition-all hover:border-foreground hover:-translate-y-0.5 ${s.span}`}
              data-testid={`service-card-${s.id}`}
            >
              <div className="flex items-start justify-between">
                <s.icon size={26} strokeWidth={1.7} className="text-foreground group-hover:copper transition-colors" />
                <span className="font-mono text-xs text-muted-foreground">{s.code}</span>
              </div>
              <div>
                <h3 className="font-display font-bold text-xl tracking-tight">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PortfolioPreview({ items }) {
  return (
    <section className="section-pad bg-secondary" data-testid="portfolio-preview-section">
      <div className="container-page">
        <div className="flex items-end justify-between mb-12 gap-6 flex-wrap">
          <div>
            <div className="overline copper mb-4">Réalisations récentes</div>
            <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight">
              Des chantiers livrés.<br/>Pas des promesses.
            </h2>
          </div>
          <Link to="/realisations" className="btn-ghost-light" data-testid="portfolio-view-all">
            Voir toutes les réalisations
            <ArrowRight size={16} strokeWidth={2.5} />
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {items.map((p) => (
            <article key={p.id} className="bg-card border border-border group" data-testid={`portfolio-preview-${p.id}`}>
              <div className="img-hover aspect-[4/3] bg-muted relative">
                <PortfolioImage item={p} />
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="overline copper">{p.category}</span>
                  {p.year && <span className="font-mono text-xs text-muted-foreground">{p.year}</span>}
                </div>
                <h3 className="font-display font-bold text-lg tracking-tight">{p.title}</h3>
                <div className="font-mono text-xs text-muted-foreground mt-2">{p.location}</div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function About() {
  const features = [
    "Devis gratuit sous 48h",
    "Garantie décennale",
    "Qualifelec certifié",
    "Prix transparents",
    "Chantiers propres",
    "Délais respectés",
  ];
  return (
    <section id="about" className="section-pad bg-background" data-testid="about-section">
      <div className="container-page grid md:grid-cols-12 gap-12 items-center">
        <div className="md:col-span-6">
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1621905251189-08b45d6a269e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxlbGVjdHJpY2lhbiUyMHdvcmtpbmd8ZW58MHx8fHwxNzgwNzAyMzY0fDA&ixlib=rb-4.1.0&q=85"
              alt="Atelier"
              className="w-full h-[480px] md:h-[560px] object-cover"
            />
            <div className="absolute -bottom-6 -right-2 md:-right-8 bg-copper text-white p-6 md:p-8">
              <div className="font-display font-black text-4xl md:text-5xl leading-none">20</div>
              <div className="overline mt-2 text-white/90">Années d'expertise</div>
            </div>
          </div>
        </div>

        <div className="md:col-span-6">
          <div className="overline copper mb-4">À propos</div>
          <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight leading-[1.05]">
            Un savoir-faire reconnu depuis 2005.
          </h2>
          <p className="mt-6 text-muted-foreground leading-relaxed">
            AMINE-ELEC intervient pour les particuliers et les professionnels sur tous types de chantiers :
            maisons individuelles, appartements, commerces et locaux tertiaires en région Rhône-Alpes.
            Notre équipe qualifiée s'engage à livrer des prestations propres, dans les délais et selon
            les normes en vigueur.
          </p>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            Chaque projet est étudié et chiffré avec soin. Nous privilégions le matériel professionnel
            (Schneider, Hager, Legrand) et nous délivrons systématiquement une attestation Consuel à la livraison.
          </p>
          <div className="mt-8 grid grid-cols-2 gap-x-6 gap-y-3">
            {features.map((f) => (
              <div key={f} className="flex items-center gap-3 text-sm">
                <span className="w-5 h-5 border border-copper text-copper flex items-center justify-center">
                  <Check size={12} strokeWidth={3} />
                </span>
                <span>{f}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Testimonials({ items, onReload }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", role: "", rating: 5, quote: "" });
  const [hover, setHover] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.quote.trim()) {
      toast.error("Nom et témoignage sont requis");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/testimonials", form);
      toast.success("Merci pour votre témoignage !");
      setForm({ name: "", role: "", rating: 5, quote: "" });
      setShowForm(false);
      onReload?.();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="section-pad bg-graphite text-white" data-testid="testimonials-section">
      <div className="container-page">
        <div className="grid md:grid-cols-12 gap-6 mb-14">
          <div className="md:col-span-7">
            <div className="overline copper mb-4">Témoignages</div>
            <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight leading-[1.05]">
              Ce que disent nos clients.
            </h2>
          </div>
          <div className="md:col-span-5 flex md:justify-end md:items-end">
            <button
              onClick={() => setShowForm((s) => !s)}
              className="btn-copper"
              data-testid="testimonial-toggle-form"
            >
              {showForm ? "Fermer" : "Laisser un témoignage"}
              <ArrowUpRight size={18} strokeWidth={2.5} />
            </button>
          </div>
        </div>

        {showForm && (
          <form
            onSubmit={submit}
            className="mb-14 border border-white/15 p-8 md:p-10 bg-white/[0.03]"
            data-testid="testimonial-form"
          >
            <h3 className="font-display font-bold text-2xl tracking-tight mb-6">Partagez votre expérience</h3>
            <div className="grid md:grid-cols-2 gap-5">
              <label className="block">
                <span className="overline text-white/60 block mb-2">Votre nom *</span>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  required
                  placeholder="Prénom Nom"
                  className="w-full px-4 py-3 bg-graphite border border-white/20 text-white placeholder-white/30 focus:outline-none focus:border-copper"
                  style={{ borderRadius: 2 }}
                  data-testid="testimonial-input-name"
                />
              </label>
              <label className="block">
                <span className="overline text-white/60 block mb-2">Statut / Ville (optionnel)</span>
                <input
                  type="text"
                  value={form.role}
                  onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                  placeholder="Ex. Particulier — Lyon"
                  className="w-full px-4 py-3 bg-graphite border border-white/20 text-white placeholder-white/30 focus:outline-none focus:border-copper"
                  style={{ borderRadius: 2 }}
                  data-testid="testimonial-input-role"
                />
              </label>
            </div>

            <div className="mt-5">
              <span className="overline text-white/60 block mb-2">Votre note *</span>
              <div className="flex gap-2" data-testid="testimonial-rating">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    type="button"
                    key={n}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    onClick={() => setForm((f) => ({ ...f, rating: n }))}
                    className="p-1"
                    data-testid={`rating-star-${n}`}
                  >
                    <Star
                      size={28}
                      strokeWidth={1.5}
                      className={n <= (hover || form.rating) ? "copper fill-current" : "text-white/30"}
                    />
                  </button>
                ))}
                <span className="ml-3 font-mono text-sm text-white/60 self-center">{form.rating}/5</span>
              </div>
            </div>

            <label className="block mt-5">
              <span className="overline text-white/60 block mb-2">Votre témoignage *</span>
              <textarea
                value={form.quote}
                onChange={(e) => setForm((f) => ({ ...f, quote: e.target.value }))}
                required
                rows={4}
                placeholder="Décrivez votre expérience avec AMINE-ELEC…"
                className="w-full px-4 py-3 bg-graphite border border-white/20 text-white placeholder-white/30 focus:outline-none focus:border-copper resize-none"
                style={{ borderRadius: 2 }}
                data-testid="testimonial-input-quote"
              />
            </label>

            <button
              type="submit"
              disabled={submitting}
              className="btn-copper mt-6 disabled:opacity-50"
              data-testid="testimonial-submit"
            >
              {submitting ? "Envoi…" : "Publier mon témoignage"}
              <ArrowUpRight size={18} strokeWidth={2.5} />
            </button>
          </form>
        )}

        {items.length === 0 ? (
          <div className="border border-white/15 p-12 md:p-20 text-center" data-testid="testimonials-empty">
            <Star size={28} className="copper mx-auto mb-4" strokeWidth={1.5} />
            <p className="font-display text-2xl md:text-3xl tracking-tight">
              Soyez le premier à partager votre expérience.
            </p>
            <p className="mt-3 text-white/60 max-w-md mx-auto">
              Votre avis nous aide à progresser et à inspirer confiance aux prochains clients.
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-px bg-white/15">
            {items.map((t, i) => (
              <article key={t.id} className="bg-graphite p-8 md:p-10 flex flex-col" data-testid={`testimonial-${i}`}>
                <div className="flex gap-1 mb-6">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Star key={j} size={14} className="copper fill-current" />
                  ))}
                </div>
                <p className="font-display text-xl md:text-2xl leading-snug tracking-tight flex-1">"{t.quote}"</p>
                <div className="mt-8 pt-6 border-t border-white/15">
                  <div className="font-semibold">{t.name}</div>
                  {t.role && <div className="overline text-white/60 mt-1">{t.role}</div>}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Nom et email sont requis");
      return;
    }
    setLoading(true);
    try {
      await api.post("/quotes", form);
      toast.success("Demande envoyée. Nous vous recontactons sous 48h.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur lors de l'envoi. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="section-pad bg-secondary" data-testid="contact-section">
      <div className="container-page grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="overline copper mb-4">Contact</div>
          <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight leading-[1.05]">
            Parlons de votre projet.
          </h2>
          <p className="mt-6 text-muted-foreground leading-relaxed">
            Besoin d'un devis, d'un conseil ou d'une intervention urgente ? Notre équipe répond
            généralement sous 24h ouvrées.
          </p>

          <div className="mt-10 space-y-6">
            <ContactItem icon={Phone} label="Téléphone" value="06 41 28 74 92" href="tel:+33641287492" />
            <ContactItem icon={Award} label="Email" value="amine.elec15@gmail.com" href="mailto:amine.elec15@gmail.com" />
            <ContactItem icon={Zap} label="Zone d'intervention" value="Région Rhône-Alpes" />
            <ContactItem icon={Clock} label="Horaires" value="Lun–Ven 8h–20h · Urgences 24/7" />
          </div>
        </div>

        <form onSubmit={submit} className="md:col-span-7 bg-card border border-border p-8 md:p-12" data-testid="quote-form">
          <h3 className="font-display font-bold text-2xl tracking-tight mb-2">Demande de devis</h3>
          <p className="text-sm text-muted-foreground mb-8">Réponse sous 48h ouvrées · Devis gratuit · Sans engagement</p>

          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Nom complet *">
              <input
                type="text"
                value={form.name}
                onChange={onChange("name")}
                required
                className="ep-input"
                placeholder="Votre nom"
                data-testid="quote-input-name"
              />
            </Field>
            <Field label="Email *">
              <input
                type="email"
                value={form.email}
                onChange={onChange("email")}
                required
                className="ep-input"
                placeholder="votre@email.fr"
                data-testid="quote-input-email"
              />
            </Field>
            <Field label="Téléphone">
              <input
                type="tel"
                value={form.phone}
                onChange={onChange("phone")}
                className="ep-input"
                placeholder="06 00 00 00 00"
                data-testid="quote-input-phone"
              />
            </Field>
            <Field label="Type de prestation">
              <select
                value={form.service}
                onChange={onChange("service")}
                className="ep-input"
                data-testid="quote-input-service"
              >
                <option value="">Sélectionner…</option>
                <option>Installation neuve</option>
                <option>Rénovation électrique</option>
                <option>Dépannage urgent</option>
                <option>Éclairage</option>
                <option>Borne de recharge</option>
                <option>Diagnostic & Conformité</option>
                <option>Autre</option>
              </select>
            </Field>
          </div>

          <Field label="Description du projet" className="mt-5">
            <textarea
              value={form.message}
              onChange={onChange("message")}
              rows={5}
              className="ep-input resize-none"
              placeholder="Décrivez votre projet, surface, contraintes éventuelles…"
              data-testid="quote-input-message"
            />
          </Field>

          <button
            type="submit"
            disabled={loading}
            className="btn-copper mt-6 w-full justify-center text-base disabled:opacity-50"
            data-testid="quote-submit"
          >
            {loading ? "Envoi…" : "Envoyer ma demande"}
            <ArrowUpRight size={18} strokeWidth={2.5} />
          </button>

          <style>{`
            .ep-input {
              width: 100%;
              padding: 0.85rem 1rem;
              border: 1px solid hsl(var(--border));
              background: #fff;
              font-family: inherit;
              font-size: 0.95rem;
              border-radius: 2px;
              transition: border-color 0.2s, box-shadow 0.2s;
            }
            .ep-input:focus {
              outline: none;
              border-color: #C25E1A;
              box-shadow: 0 0 0 1px #C25E1A;
            }
          `}</style>
        </form>
      </div>
    </section>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="overline text-foreground/70 mb-2 block">{label}</span>
      {children}
    </label>
  );
}

function ContactItem({ icon: Icon, label, value, href }) {
  const content = (
    <div className="flex items-start gap-4">
      <div className="w-11 h-11 border border-border flex items-center justify-center shrink-0">
        <Icon size={18} className="copper" strokeWidth={2} />
      </div>
      <div>
        <div className="overline text-muted-foreground mb-1">{label}</div>
        <div className="font-medium">{value}</div>
      </div>
    </div>
  );
  return href ? (
    <a href={href} className="block hover:opacity-80 transition-opacity">{content}</a>
  ) : content;
}
